import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth, type Role } from '../lib/auth'
import { FirebaseError } from 'firebase/app'

export default function Login() {
  const { login, loginGoogle } = useAuth()
  const nav = useNavigate()
  const loc = useLocation() as { state: { from?: string } | null }
  const [searchParams] = useSearchParams()
  const roleParam: Role = searchParams.get('role') === 'medico' ? 'medico' : 'paciente'
  const dest = loc.state?.from || (roleParam === 'medico' ? '/medico' : '/app')

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submittingGoogle, setSubmittingGoogle] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    setSubmitting(true)
    try {
      await login(email, senha)
      nav(dest, { replace: true })
    } catch (e) {
      setErro(traduzErro(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle() {
    setErro(null)
    setSubmittingGoogle(true)
    try {
      await loginGoogle(roleParam)
      nav(dest, { replace: true })
    } catch (e) {
      setErro(traduzErro(e))
    } finally {
      setSubmittingGoogle(false)
    }
  }

  return (
    <AuthShell titulo="Entrar">
      <button
        type="button"
        onClick={handleGoogle}
        disabled={submittingGoogle || submitting}
        className="btn-ghost w-full mb-4"
      >
        <GoogleIcon />
        {submittingGoogle ? 'Conectando…' : 'Continuar com Google'}
      </button>

      <Divisor />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="email">E-mail</label>
          <input
            id="email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label className="label" htmlFor="senha">Senha</label>
            <Link to="/esqueci-senha" className="text-xs font-semibold text-anamis-600 hover:underline">
              Esqueci minha senha
            </Link>
          </div>
          <input
            id="senha"
            type="password"
            className="input"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            minLength={6}
          />
        </div>
        {erro && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={submitting || submittingGoogle}>
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-600">
        Não tem conta?{' '}
        <Link to="/cadastro" className="text-anamis-600 font-semibold hover:underline">
          Criar conta
        </Link>
      </p>
    </AuthShell>
  )
}

export function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

export function Divisor() {
  return (
    <div className="relative flex items-center my-4">
      <div className="flex-grow border-t border-slate-200" />
      <span className="mx-4 text-xs uppercase tracking-wider text-slate-400 font-semibold">ou</span>
      <div className="flex-grow border-t border-slate-200" />
    </div>
  )
}

function AuthShell({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-anamis-50 to-white">
      <header className="px-6 py-5 max-w-6xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-8 h-8 rounded-lg bg-anamis-600 flex items-center justify-center text-white font-bold">A</div>
          <span className="font-bold text-xl text-anamis-800">anamis</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-8 text-center">{titulo}</h1>
          <div className="card">{children}</div>
        </div>
      </main>
    </div>
  )
}

function traduzErro(e: unknown): string {
  if (e instanceof FirebaseError) {
    const map: Record<string, string> = {
      'auth/invalid-credential': 'E-mail ou senha incorretos.',
      'auth/invalid-email': 'E-mail inválido.',
      'auth/user-not-found': 'Usuário não encontrado.',
      'auth/wrong-password': 'Senha incorreta.',
      'auth/too-many-requests': 'Muitas tentativas. Tente novamente em alguns minutos.',
      'auth/network-request-failed': 'Falha de conexão. Verifique sua internet.',
      'auth/email-already-in-use': 'Este e-mail já está cadastrado.',
      'auth/weak-password': 'Senha muito fraca (mínimo 6 caracteres).',
      'auth/popup-closed-by-user': 'Login cancelado.',
      'auth/popup-blocked': 'Pop-up bloqueado pelo navegador. Permita pop-ups e tente novamente.',
      'auth/account-exists-with-different-credential': 'Este e-mail já tem conta com outro método. Entre com e-mail/senha.',
    }
    return map[e.code] || `Erro: ${e.code}`
  }
  return 'Erro inesperado. Tente novamente.'
}

export { traduzErro }
